import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Loader2, Mail, Paperclip } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

// Alle e-mails die met dit adres gewisseld zijn (Postvak IN, Verzonden, Archief), klik opent de mail in de mailbox.
export default function GuestMails({ email, onNavigate }) {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const allowed = ["admin", "superadmin"].includes(user?.role) || user?.mailbox_access;

  useEffect(() => {
    if (!email || !allowed) return;
    setRows(null); setErr("");
    api.get("/mailbox/with", { params: { email } }).then(({ data }) => setRows(data))
      .catch((e) => { setErr(e.response?.data?.detail || t("mb_not_configured")); setRows([]); });
  }, [email, allowed, t]);

  if (!email) return <p className="text-sm text-muted-foreground p-3" data-testid="guest-mails-noemail">{t("mb_no_email")}</p>;
  if (!allowed) return <p className="text-sm text-muted-foreground p-3" data-testid="guest-mails-noaccess">{t("mb_no_access")}</p>;
  if (rows === null) return <p className="text-sm text-muted-foreground p-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("loading")}</p>;
  if (err) return <p className="text-sm text-muted-foreground p-3" data-testid="guest-mails-error">{String(err)}</p>;
  if (!rows.length) return <p className="text-sm text-muted-foreground p-3" data-testid="guest-mails-empty">{t("mb_none_with_guest")}</p>;

  return (
    <ul className="divide-y divide-border/60 rounded-xl border border-border max-h-[50vh] overflow-y-auto cb-scroll" data-testid="guest-mails">
      {rows.map((m) => (
        <li key={`${m.folder}-${m.uid}`}>
          <button type="button" data-testid={`guest-mail-${m.folder}-${m.uid}`}
            onClick={() => { onNavigate && onNavigate(); navigate(`/mailbox?folder=${m.folder}&open=${m.uid}`); }}
            className="w-full text-left px-3 py-2 hover:bg-secondary/60 transition-colors flex items-start gap-2">
            {m.direction === "out"
              ? <ArrowUpRight className="h-4 w-4 mt-0.5 shrink-0 text-sky-600" title={t("mb_sent")} />
              : <ArrowDownLeft className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" title={t("mb_inbox")} />}
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className={`truncate text-sm ${m.seen ? "font-medium" : "font-bold"}`}>{m.subject}</span>
                {m.has_attachments && <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />}
                <span className="ml-auto text-[11px] font-mono text-muted-foreground shrink-0">{m.date ? new Date(m.date).toLocaleDateString() : ""}</span>
              </span>
              <span className="block text-xs text-muted-foreground truncate">
                {m.direction === "out" ? `${t("mb_to")}: ${m.to.map((x) => x.email).join(", ")}` : `${m.from.name || m.from.email}`} · {t(`mb_${m.folder}`)}
              </span>
            </span>
          </button>
        </li>
      ))}
      <li className="px-3 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />{email}</li>
    </ul>
  );
}
